import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BetaTag, BrandMark } from '../components/BrandMark';
import { BetaNote } from '../components/BetaNote';
import { Link } from 'react-router-dom';
import {
    AlertTriangle, Check, ChevronDown, ChevronRight, Clock, FolderOpen, FolderPlus, FolderTree as FolderTreeIcon, HardDrive, LayoutGrid, Palette, Pencil,
    Pin, PinOff, Plus, Search, Sparkles, Star, Trash2, Users, X, FolderInput, UserMinus, ExternalLink, FolderKanban, Inbox, Copy, Send, Loader2,
} from 'lucide-react';
import { PeopleBadge, ProjectTile, WorkspaceThumb } from '../components/library/ProjectTile';
import type { TreeLeaf } from '../components/library/FolderTree';
import { ProfileMenu } from '../components/ProfileMenu';
import { useToast } from '../components/ToastProvider';
import { usePageMeta, PAGE_META } from '../hooks/usePageMeta';
import type { WorkspaceFolder, FolderColor, FolderKind } from '../utils/workspaceFolders';
import {
    buildChildrenMap, canCreateFolderIn, canMoveFolder, collectWorkspaceIdsInFolder, getDescendantFolderIds,
    getFolderById, getFolderPath, getWorkspaceCostsByFolder, getWorkspaceCountsByFolder, makeUniqueSiblingName, MAX_FOLDER_DEPTH,
} from '../utils/folderTree';
import {
    type LibraryLocation, type LibrarySort, type LibraryWorkspace, ROOT_LOCATION, describeLocation, formatCost, formatRelativeTime,
    matchesQuery, pluralize, readExpandedFolderIds, readLibraryPrefs, readStoredLocation, recentWorkspaces, resolveLocation,
    sortWorkspaces, writeExpandedFolderIds, writeLibraryPrefs, writeStoredLocation,
} from '../utils/workspaceLibrary';
import { PageScrollLock } from '../components/PageScrollLock';
import type { WorkspacesPageProps, WorkspaceRef, DeleteFolderMode, DeleteFolderProgress } from '../components/library/types';
import { FolderTree } from '../components/library/FolderTree';
import { WorkspaceCard } from '../components/library/WorkspaceCard';
import { ProjectCard } from '../components/library/ProjectCard';
import { FolderTile } from '../components/library/FolderTile';
import { LibraryMenu, type MenuEntry } from '../components/library/LibraryMenu';
import { ProjectPanel } from '../components/library/ProjectPanel';
import { ColorDialog, ConfirmDialog, DeleteFolderDialog, MoveDialog, NameDialog, describeFolder } from '../components/library/LibraryDialogs';
import { colorTone, nextProjectColor } from '../components/library/colors';
import { ShareModal } from '../components/ShareModal';
import { SendCopyDialog } from '../components/library/SendCopyDialog';
import type { ProjectInvitation } from '../utils/storage';
import { copyErrorText, type ProjectCopy } from '../utils/projectCopies';

// ── Local types ──────────────────────────────────────────────────────────────

type DialogState =
    | { kind: 'none' }
    | { kind: 'new-workspace'; folderId: string | null }
    | { kind: 'new-folder'; parentId: string | null; folderKind: FolderKind }
    | { kind: 'rename-workspace'; workspace: LibraryWorkspace }
    | { kind: 'rename-folder'; folder: WorkspaceFolder }
    | { kind: 'color-folder'; folder: WorkspaceFolder }
    | { kind: 'move-workspace'; refs: WorkspaceRef[]; label: string; currentFolderId: string | null }
    | { kind: 'move-folder'; folder: WorkspaceFolder }
    | { kind: 'delete-workspace'; workspace: LibraryWorkspace }
    | { kind: 'leave-shared'; workspace: LibraryWorkspace }
    | { kind: 'leave-project'; project: WorkspaceFolder }
    | { kind: 'delete-folder'; folder: WorkspaceFolder };

type DragPayload =
    | { kind: 'workspace'; id: string; isLocal: boolean; folderId: string | null; name: string }
    | { kind: 'folder'; id: string; name: string };

/** No projects shared with this person */
const NO_FOLDERS: WorkspaceFolder[] = [];
const NO_INVITATIONS: ProjectInvitation[] = [];
const NO_COPIES: ProjectCopy[] = [];

const errorText = (e: unknown, fallback: string): string => (e instanceof Error && e.message ? e.message : fallback);

/** "612 MB", "1 GB", "4.2 GB" */
const formatBytes = (bytes: number): string => {
    if (bytes >= 1024 * 1024 * 1024) { const gb = bytes / (1024 * 1024 * 1024); return `${gb >= 10 ? Math.round(gb) : Math.round(gb * 10) / 10} GB`; }
    return `${Math.max(0, Math.round(bytes / (1024 * 1024)))} MB`;
};

const shortMoveReason = (reason?: string): string | undefined => {
    if (!reason) return undefined;
    if (/already/i.test(reason)) return 'Already here';
    if (/itself/i.test(reason)) return 'Inside itself';
    if (/nested|deep/i.test(reason)) return 'Too deep';
    if (/top level|inside a project/i.test(reason)) return 'Not allowed';
    return reason;
};

const SectionHeader: React.FC<{ title: string; count?: number; action?: React.ReactNode; id?: string }> = ({ title, count, action, id }) => (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-3 min-h-[36px]">
        <h2 id={id} className="ns-t-label flex items-center gap-2 whitespace-nowrap">
            {title}
            {typeof count === 'number' && <span className="text-ink-soft font-medium tracking-normal normal-case tabular-nums">{count}</span>}
        </h2>
        {action && <div className="flex flex-wrap items-center gap-2 [&_button]:whitespace-nowrap">{action}</div>}
    </div>
);

/** The cream tile an empty state or the load error lies on, so the place still reads as a place on the page. */
const EMPTY_TILE = 'ns-empty rounded-2xl bg-cream-200 px-6 py-10';

/** Nothing here yet, said the one way: a round mark, a title, a line, at most one button. */
const EmptyTile: React.FC<{ icon: React.ReactNode; title: string; body?: string; action?: React.ReactNode }> = ({ icon, title, body, action }) => (
    <div className={EMPTY_TILE}>
        <span className="ns-mark ns-mark-lg ns-mark-lavender" aria-hidden="true">{icon}</span>
        <p className="ns-empty-title">{title}</p>
        {body && <p className="ns-empty-text">{body}</p>}
        {action && <div className="mt-3">{action}</div>}
    </div>
);

const SkeletonGrid: React.FC<{ count: number; square?: boolean }> = ({ count, square = true }) => (
    <div className="ns-lib-grid-cards" aria-hidden="true">
        {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="ns-lib-card">
                <div className={`ns-skeleton ${square ? 'aspect-square' : 'h-24'} !rounded-none`} />
                <div className="p-3 flex flex-col gap-2">
                    <div className="ns-skeleton h-3.5 w-3/4" />
                    <div className="ns-skeleton h-3 w-1/2" />
                </div>
            </div>
        ))}
    </div>
);

// ── Page ─────────────────────────────────────────────────────────────────────

export const WorkspacesPage: React.FC<WorkspacesPageProps> = (props) => {
    const {
        user, isPro = false, workspaces, folders, isLoading, loadError,
        onOpenWorkspace, onCreateWorkspace, onImportFile, onRenameWorkspace, onMoveWorkspaces, onToggleFavorite,
        onDeleteWorkspace, onLeaveSharedWorkspace, onCreateFolder, onRenameFolder, onRecolorFolder, onMoveFolder,
        onDeleteFolder, onRefresh, onSignIn, onSignOut, onSubscription, onAnalytics, onSettings, bell, jump = null, onJumped,
        sharedFolders = NO_FOLDERS, onLeaveSharedProject, canShareProjects = false,
        projectInvitations = NO_INVITATIONS, onAnswerProjectInvitation, localFolderName = null, storage = null,
        projectCopies, onSendProjectCopy, onAnswerProjectCopy, onCancelProjectCopy,
    } = props;

    usePageMeta(PAGE_META.workspace);
    const toast = useToast();

    // ── Location, expansion, prefs ──────────────────────────────────────────
    // (a page opened by a link starts where the link sends it, so nothing else shows first)
    const [location, setLocationState] = useState<LibraryLocation>(() => jump?.location ?? readStoredLocation(user.sub) ?? ROOT_LOCATION);
    const [search, setSearch] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(() => readExpandedFolderIds());
    const [sort, setSortState] = useState<LibrarySort>(() => readLibraryPrefs().sort);
    // What this person pinned to the top of the sidebar (kept on this device)
    const [pinned, setPinned] = useState<string[]>(() => readLibraryPrefs().pinned);
    const [projectFilter, setProjectFilter] = useState('');
    // Past eight projects the page lists them as a table, the first eight until asked for all
    const [showAllProjects, setShowAllProjects] = useState(false);
    const searchRef = useRef<HTMLInputElement>(null);
    const mainRef = useRef<HTMLElement>(null);

    const setLocation = useCallback((next: LibraryLocation) => {
        // Carry the ancestor chain in memory too, so a folder deleted on another device
        // while we look at it falls back to the nearest survivor (not only after a reload).
        const withPath: LibraryLocation = { ...next, path: next.folderId ? getFolderPath(folders, next.folderId).map(f => f.id) : undefined };
        setLocationState(withPath);
        writeStoredLocation(user.sub, withPath, folders);
        setSearch('');
        setDrawerOpen(false);
        requestAnimationFrame(() => {
            // (the workspaces' own column scrolls, not the window: a new place starts at its top)
            const main = mainRef.current;
            if (main && main.scrollTop > 0 && typeof main.scrollTo === 'function') main.scrollTo({ top: 0 });
        });
    }, [user.sub, folders]);

    const goToFolder = useCallback((id: string | null) => setLocation({ view: 'library', folderId: id }), [setLocation]);

    // A link's place, gone to once (also while the page is already open), then handed back
    useEffect(() => {
        if (!jump) return;
        setLocation(jump.location);
        onJumped?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jump]);

    useEffect(() => {
        if (isLoading) return;
        const resolved: LibraryLocation = location.view === 'shared' && location.folderId
            ? (getFolderById(sharedFolders, location.folderId) ? location : { view: 'shared', folderId: null })
            : resolveLocation(location, folders);
        if (resolved.view !== location.view || resolved.folderId !== location.folderId) {
            setLocationState(resolved);
            writeStoredLocation(user.sub, resolved, folders);
            if (location.folderId) toast.info('That folder no longer exists', 'Showing the closest place that still does.');
        }
    }, [isLoading, folders, sharedFolders, location, user.sub, toast]);

    // The current folder and its ancestors stay open in the tree.
    useEffect(() => {
        if (!location.folderId) return;
        const path = getFolderPath(folders, location.folderId);
        if (path.length === 0) return;
        setExpandedIds(prev => {
            const next = new Set(prev);
            let changed = false;
            path.forEach(f => { if (!next.has(f.id)) { next.add(f.id); changed = true; } });
            if (changed) writeExpandedFolderIds(next);
            return changed ? next : prev;
        });
    }, [location.folderId, folders]);

    const toggleExpand = useCallback((id: string) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            writeExpandedFolderIds(next);
            return next;
        });
    }, []);

    const setSort = (s: LibrarySort) => { setSortState(s); writeLibraryPrefs({ sort: s, pinned }); };
    const isPinned = (id: string) => pinned.includes(id);
    const togglePin = (id: string) => {
        setPinned(prev => {
            const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id].slice(-12);
            writeLibraryPrefs({ sort, pinned: next });
            return next;
        });
    };

    // Phone drawer: Escape closes, Tab stays inside, focus moves in on open and back to the opener on close.
    const drawerRef = useRef<HTMLDivElement>(null);
    const drawerCloseRef = useRef<HTMLButtonElement>(null);
    const drawerOpenerRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (!drawerOpen) return;
        const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const onKey = (e: KeyboardEvent) => {
            // A dialog or menu stacked on top owns Escape/Tab
            if (document.querySelector('[role="dialog"][aria-modal="true"]:not(.ns-lib-drawer), [role="menu"]')) return;
            if (e.key === 'Escape') { e.stopPropagation(); setDrawerOpen(false); return; }
            if (e.key === 'Tab' && drawerRef.current) {
                const nodes = Array.from(drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(n => n.offsetParent !== null);
                if (nodes.length === 0) return;
                const first = nodes[0];
                const last = nodes[nodes.length - 1];
                const active = document.activeElement as HTMLElement | null;
                if (!drawerRef.current.contains(active)) { e.preventDefault(); first.focus(); return; }
                if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
                else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
            }
        };
        document.addEventListener('keydown', onKey);
        const raf = requestAnimationFrame(() => drawerCloseRef.current?.focus());
        return () => {
            document.removeEventListener('keydown', onKey);
            cancelAnimationFrame(raf);
            requestAnimationFrame(() => drawerOpenerRef.current?.focus());
        };
    }, [drawerOpen]);

    // "/" focuses search anywhere on the page
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
            const target = e.target as HTMLElement | null;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
            // Never pull focus out of an open dialog, drawer or menu
            if (document.querySelector('[aria-modal="true"], [role="menu"]')) return;
            e.preventDefault();
            searchRef.current?.focus();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    // ── Derived data ────────────────────────────────────────────────────────
    const folderIdSet = useMemo(() => new Set(folders.map(f => f.id)), [folders]);
    const owned = useMemo(() => workspaces.filter(w => !w.isShared), [workspaces]);
    const shared = useMemo(() => workspaces.filter(w => w.isShared), [workspaces]);
    // Projects others shared with this person: their workspaces are shown inside them, not one by one
    const sharedFolderIds = useMemo(() => new Set(sharedFolders.map(f => f.id)), [sharedFolders]);
    const sharedProjects = useMemo(() => sharedFolders.filter(f => f.kind === 'project'), [sharedFolders]);
    const sharedInProjects = useMemo(() => shared.filter(w => !!w.sharedProjectId && !!w.folderId && sharedFolderIds.has(w.folderId)), [shared, sharedFolderIds]);
    const sharedDirect = useMemo(() => shared.filter(w => !sharedInProjects.includes(w)), [shared, sharedInProjects]);
    const sharedCount = sharedProjects.length + sharedDirect.length;
    /** Invitations to answer: shown first under Shared with me */
    const invitations = onAnswerProjectInvitation ? projectInvitations : NO_INVITATIONS;
    const [answering, setAnswering] = useState<string | null>(null);
    /** Copies of whole projects waiting for this person's answer, and the ones they sent */
    const copiesForMe = onAnswerProjectCopy ? (projectCopies?.received ?? NO_COPIES) : NO_COPIES;
    const copiesSent = projectCopies?.sent ?? NO_COPIES;
    /** A copy being made right now, as the server last reported it */
    const [copying, setCopying] = useState<{ id: string; copy: ProjectCopy } | null>(null);
    const sharedChildren = useMemo(() => buildChildrenMap(sharedFolders), [sharedFolders]);
    const sharedCounts = useMemo(() => getWorkspaceCountsByFolder(sharedFolders, sharedInProjects), [sharedFolders, sharedInProjects]);
    const sharedCosts = useMemo(() => getWorkspaceCostsByFolder(sharedFolders, sharedInProjects), [sharedFolders, sharedInProjects]);
    const allFolders = useMemo(() => [...folders, ...sharedFolders], [folders, sharedFolders]);
    const sharedCurrent = location.view === 'shared' ? getFolderById(sharedFolders, location.folderId) : undefined;
    /** What others shared, by who shared it (the owner, when that is all the record says): their projects, then their workspaces, the ones changed last first */
    const sharedByPerson = useMemo(() => {
        const groups = new Map<string, { projects: WorkspaceFolder[]; workspaces: LibraryWorkspace[] }>();
        const groupOf = (by: string) => {
            const group = groups.get(by) ?? { projects: [], workspaces: [] };
            groups.set(by, group);
            return group;
        };
        for (const p of sharedProjects) groupOf(p.sharedBy || 'Someone').projects.push(p);
        for (const w of sharedDirect) groupOf(w.sharedBy || w.ownerName || 'Someone').workspaces.push(w);
        return Array.from(groups, ([by, group]) => ({
            by,
            projects: [...group.projects].sort((a, b) => a.name.localeCompare(b.name)),
            workspaces: [...group.workspaces].sort((a, b) => (b.lastSaved || 0) - (a.lastSaved || 0)),
        })).sort((a, b) => a.by.localeCompare(b.by));
    }, [sharedProjects, sharedDirect]);
    const counts = useMemo(() => getWorkspaceCountsByFolder(folders, owned), [folders, owned]);
    // What each project's and folder's workspaces cost together
    const costs = useMemo(() => getWorkspaceCostsByFolder(folders, owned), [folders, owned]);
    const childrenMap = useMemo(() => buildChildrenMap(folders), [folders]);
    const roots = useMemo(() => childrenMap.get(null) || [], [childrenMap]);
    const projects = useMemo(() => roots.filter(f => f.kind === 'project'), [roots]);
    // Folders whose project was deleted elsewhere: shown separately so they can be re-homed.
    const orphanFolders = useMemo(() => roots.filter(f => f.kind !== 'project'), [roots]);
    const unfiled = useMemo(() => owned.filter(w => !w.folderId || !folderIdSet.has(w.folderId)), [owned, folderIdSet]);
    const starred = useMemo(() => owned.filter(w => w.isFavorite), [owned]);
    const recent = useMemo(() => recentWorkspaces(workspaces, 8), [workspaces]);
    const currentFolder = getFolderById(folders, location.folderId);
    const currentPath = useMemo(() => getFolderPath(folders, location.folderId), [folders, location.folderId]);
    const currentProject = currentPath[0];
    const isFirstRun = !isLoading && !loadError && workspaces.length === 0 && folders.length === 0;

    const workspacesIn = useCallback((folderId: string) => owned.filter(w => w.folderId === folderId), [owned]);
    const previewsFor = useCallback((folderId: string) =>
        collectWorkspaceIdsInFolder(folders, owned, folderId)
            .filter(w => !!w.thumbnail)
            .sort((a, b) => b.lastSaved - a.lastSaved)
            .slice(0, 4)
            .map(w => w.thumbnail as string),
    [folders, owned]);
    /** When a workspace anywhere in a folder was last saved */
    const editedAtIn = useCallback((folderId: string): number | undefined =>
        collectWorkspaceIdsInFolder(folders, owned, folderId).reduce<number | undefined>((latest, w) => (w.lastSaved > (latest ?? 0) ? w.lastSaved : latest), undefined),
    [folders, owned]);
    /** The colour of the project a workspace is in (its dot on the cards) */
    const colorOf = useCallback((w: LibraryWorkspace): FolderColor | undefined => (w.folderId ? getFolderPath(folders, w.folderId)[0]?.color : undefined), [folders]);
    /** Every owned workspace under its folder, for the sidebar's tree (a project unfolds into its folders and its workspaces) */
    const treeLeaves = useMemo(() => {
        const map = new Map<string | null, TreeLeaf[]>();
        for (const w of owned) {
            if (!w.folderId || !folderIdSet.has(w.folderId)) continue;
            const list = map.get(w.folderId) || [];
            list.push({ id: w.id, label: w.name, thumbnail: w.thumbnail, note: w.isLocal ? 'On this device' : undefined, cost: w.totalCost });
            map.set(w.folderId, list);
        }
        for (const list of map.values()) list.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
        return map;
    }, [owned, folderIdSet]);
    /** The workspaces of the projects shared with this person, under their folders, for the sidebar's outline of those projects */
    const sharedTreeLeaves = useMemo(() => {
        const map = new Map<string | null, TreeLeaf[]>();
        for (const w of sharedInProjects) {
            if (!w.folderId) continue;
            const list = map.get(w.folderId) || [];
            list.push({ id: w.id, label: w.name, thumbnail: w.thumbnail, cost: w.totalCost });
            map.set(w.folderId, list);
        }
        for (const list of map.values()) list.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
        return map;
    }, [sharedInProjects]);
    /** The shared project each shared folder belongs to (its root), for grouping the outline by who shared it */
    const sharedProjectOf = useMemo(() => new Map(sharedFolders.map(f => [f.id, getFolderPath(sharedFolders, f.id)[0]?.id ?? f.id])), [sharedFolders]);
    const pinnedProjects = useMemo(() => projects.filter(p => pinned.includes(p.id)), [projects, pinned]);
    const pinnedFolders = useMemo(() => folders.filter(f => f.kind === 'folder' && pinned.includes(f.id)), [folders, pinned]);
    const pinnedWorkspaces = useMemo(() => workspaces.filter(w => pinned.includes(w.id)), [workspaces, pinned]);

    // ── Dialogs ─────────────────────────────────────────────────────────────
    const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
    /** One of this person's projects, being shared */
    const [sharingProject, setSharingProject] = useState<WorkspaceFolder | null>(null);
    /** One of this person's projects, a copy of it being sent */
    const [copyingProject, setCopyingProject] = useState<WorkspaceFolder | null>(null);
    // A dialog always sits above the phone drawer; close the drawer so the two never stack.
    useEffect(() => { if (dialog.kind !== 'none') setDrawerOpen(false); }, [dialog.kind]);
    const [newLocation, setNewLocation] = useState<string | null>(null);
    const [newColor, setNewColor] = useState<FolderColor>('lavender');
    const closeDialog = () => setDialog({ kind: 'none' });

    const openNewWorkspace = (folderId: string | null) => { setNewLocation(folderId); setDialog({ kind: 'new-workspace', folderId }); };
    const openNewFolder = (parentId: string | null) => {
        if (parentId === null) setNewColor(nextProjectColor(projects.length));
        setDialog({ kind: 'new-folder', parentId, folderKind: parentId === null ? 'project' : 'folder' });
    };

    const siblingNames = (parentId: string | null, excludeId?: string) =>
        (childrenMap.get(parentId) || []).filter(f => f.id !== excludeId).map(f => f.name);
    const validateFolderName = (parentId: string | null, kind: FolderKind, excludeId?: string) => (name: string) =>
        siblingNames(parentId, excludeId).some(n => n.toLowerCase() === name.toLowerCase())
            ? `A ${kind} with this name already exists here.`
            : undefined;

    const newFolderReason = (parentId: string | null): string | undefined => {
        if (parentId === null) return undefined;
        const check = canCreateFolderIn(folders, parentId);
        return check.ok ? undefined : check.reason;
    };

    /** Move a folder; if a sibling already has its name, rename it "Name 2" and say so. */
    const moveFolderTo = async (f: WorkspaceFolder, targetId: string): Promise<string> => {
        const unique = makeUniqueSiblingName(f.name, siblingNames(targetId, f.id));
        await onMoveFolder(f.id, targetId);
        if (unique !== f.name) await onRenameFolder(f.id, unique);
        const destination = describeFolder(folders, targetId);
        toast.success('Moved', `“${f.name}” is now in ${destination}.${unique !== f.name ? ` Renamed to “${unique}”.` : ''}`);
        return unique;
    };

    // ── Drag and drop (desktop enhancement; Move… is the guaranteed path) ───
    const dragRef = useRef<DragPayload | null>(null);
    const [draggingId, setDraggingId] = useState<string | null>(null);
    const [dropTarget, setDropTarget] = useState<string | null | undefined>(undefined);

    const canDropOn = useCallback((targetId: string | null): boolean => {
        const p = dragRef.current;
        if (!p) return false;
        if (p.kind === 'workspace') return targetId !== p.folderId;
        return targetId !== null && canMoveFolder(folders, p.id, targetId).ok;
    }, [folders]);

    const handleDragOver = useCallback((e: React.DragEvent, targetId: string | null) => {
        if (!dragRef.current || !canDropOn(targetId)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropTarget(prev => (prev === targetId ? prev : targetId));
    }, [canDropOn]);

    const handleDragLeave = useCallback((e: React.DragEvent, targetId: string | null) => {
        const related = e.relatedTarget as Node | null;
        if (related && e.currentTarget.contains(related)) return;
        setDropTarget(prev => (prev === targetId ? undefined : prev));
    }, []);

    const endDrag = useCallback(() => {
        dragRef.current = null;
        setDraggingId(null);
        setDropTarget(undefined);
    }, []);

    const handleDrop = useCallback(async (e: React.DragEvent, targetId: string | null) => {
        e.preventDefault();
        e.stopPropagation();
        const p = dragRef.current;
        const allowed = canDropOn(targetId);
        endDrag();
        if (!p || !allowed) return;
        const destination = describeFolder(folders, targetId);
        try {
            if (p.kind === 'workspace') {
                await onMoveWorkspaces([{ id: p.id, isLocal: p.isLocal }], targetId);
                toast.success('Moved', `“${p.name}” is now in ${destination}.`);
            } else if (targetId) {
                const f = getFolderById(folders, p.id);
                if (f) await moveFolderTo(f, targetId);
            }
        } catch (err) {
            toast.error('Could not move', errorText(err, 'Please try again.'));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canDropOn, endDrag, folders, onMoveWorkspaces, toast]);

    const startDrag = (e: React.DragEvent, payload: DragPayload) => {
        dragRef.current = payload;
        setDraggingId(payload.id);
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', payload.name); } catch { /* older browsers */ }
    };

    const dropProps = (targetId: string | null) => ({
        active: dropTarget !== undefined && dropTarget === targetId,
        onDragOver: (e: React.DragEvent) => handleDragOver(e, targetId),
        onDragLeave: (e: React.DragEvent) => handleDragLeave(e, targetId),
        onDrop: (e: React.DragEvent) => { void handleDrop(e, targetId); },
    });

    const treeDrop = useMemo(() => ({
        activeTargetId: dropTarget,
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: (e: React.DragEvent, id: string | null) => { void handleDrop(e, id); },
    }), [dropTarget, handleDragOver, handleDragLeave, handleDrop]);

    // ── Menus ───────────────────────────────────────────────────────────────
    const workspaceMenu = (w: LibraryWorkspace): MenuEntry[] => {
        const ref: WorkspaceRef = { id: w.id, isLocal: w.isLocal };
        // Shared as part of a project: it is left with the project
        if (w.isShared && w.sharedProjectId) {
            return [{ id: 'open', label: 'Open', icon: <ExternalLink className="w-4 h-4" />, onSelect: () => onOpenWorkspace(w.id, w.isLocal) }];
        }
        if (w.isShared) {
            return [
                { id: 'open', label: 'Open', icon: <ExternalLink className="w-4 h-4" />, onSelect: () => onOpenWorkspace(w.id, w.isLocal) },
                'separator',
                { id: 'leave', label: 'Leave…', icon: <UserMinus className="w-4 h-4" />, danger: true, onSelect: () => setDialog({ kind: 'leave-shared', workspace: w }) },
            ];
        }
        return [
            { id: 'open', label: 'Open', icon: <ExternalLink className="w-4 h-4" />, onSelect: () => onOpenWorkspace(w.id, w.isLocal) },
            { id: 'rename', label: 'Rename…', icon: <Pencil className="w-4 h-4" />, onSelect: () => setDialog({ kind: 'rename-workspace', workspace: w }) },
            { id: 'move', label: 'Move to…', icon: <FolderInput className="w-4 h-4" />, onSelect: () => setDialog({ kind: 'move-workspace', refs: [ref], label: w.name, currentFolderId: w.folderId }) },
            { id: 'star', label: w.isFavorite ? 'Unstar' : 'Star', icon: <Star className="w-4 h-4" />, onSelect: () => { void toggleStar(w); } },
            { id: 'pin', label: isPinned(w.id) ? 'Unpin from the sidebar' : 'Pin to the sidebar', icon: isPinned(w.id) ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />, onSelect: () => togglePin(w.id) },
            'separator',
            { id: 'delete', label: 'Delete…', icon: <Trash2 className="w-4 h-4" />, danger: true, onSelect: () => setDialog({ kind: 'delete-workspace', workspace: w }) },
        ];
    };

    const folderMenu = (f: WorkspaceFolder): MenuEntry[] => {
        const reason = newFolderReason(f.id);
        const items: MenuEntry[] = [
            { id: 'open', label: 'Open', icon: <FolderOpen className="w-4 h-4" />, onSelect: () => goToFolder(f.id) },
            { id: 'rename', label: 'Rename…', icon: <Pencil className="w-4 h-4" />, onSelect: () => setDialog({ kind: 'rename-folder', folder: f }) },
        ];
        if (f.kind === 'project') items.push({ id: 'color', label: 'Change colour…', icon: <Palette className="w-4 h-4" />, onSelect: () => setDialog({ kind: 'color-folder', folder: f }) });
        if (f.kind === 'project' && canShareProjects) items.push({ id: 'share', label: 'Share…', icon: <Users className="w-4 h-4" />, onSelect: () => setSharingProject(f) });
        if (f.kind === 'project' && canShareProjects && onSendProjectCopy) items.push({ id: 'copy', label: 'Send a copy…', icon: <Send className="w-4 h-4" />, onSelect: () => setCopyingProject(f) });
        items.push({ id: 'pin', label: isPinned(f.id) ? 'Unpin from the sidebar' : 'Pin to the sidebar', icon: isPinned(f.id) ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />, onSelect: () => togglePin(f.id) });
        items.push({ id: 'new-folder', label: 'New folder…', icon: <FolderPlus className="w-4 h-4" />, disabled: !!reason, disabledReason: reason, onSelect: () => openNewFolder(f.id) });
        items.push({ id: 'new-workspace', label: 'New workspace…', icon: <Plus className="w-4 h-4" />, onSelect: () => openNewWorkspace(f.id) });
        if (f.kind === 'folder') items.push({ id: 'move', label: 'Move to…', icon: <FolderInput className="w-4 h-4" />, onSelect: () => setDialog({ kind: 'move-folder', folder: f }) });
        items.push('separator');
        items.push({ id: 'delete', label: 'Delete…', icon: <Trash2 className="w-4 h-4" />, danger: true, onSelect: () => setDialog({ kind: 'delete-folder', folder: f }) });
        return items;
    };

    /** A project someone shared with this person: it can be opened, and left */
    const sharedProjectMenu = (p: WorkspaceFolder): MenuEntry[] => [
        { id: 'open', label: 'Open', icon: <FolderOpen className="w-4 h-4" />, onSelect: () => setLocation({ view: 'shared', folderId: p.id }) },
        ...(onLeaveSharedProject ? ['separator' as const, { id: 'leave', label: 'Leave project…', icon: <UserMinus className="w-4 h-4" />, danger: true, onSelect: () => setDialog({ kind: 'leave-project', project: p }) }] : []),
    ];

    const newMenu: MenuEntry[] = (() => {
        const inFolder = location.view === 'library' ? location.folderId : null;
        const folderReason = inFolder ? newFolderReason(inFolder) : 'Open a project or folder first';
        return [
            { id: 'ws', label: 'New workspace…', icon: <Plus className="w-4 h-4" />, onSelect: () => openNewWorkspace(inFolder) },
            { id: 'folder', label: 'New folder…', icon: <FolderPlus className="w-4 h-4" />, disabled: !!folderReason, disabledReason: folderReason, onSelect: () => openNewFolder(inFolder) },
            { id: 'project', label: 'New project…', icon: <FolderKanban className="w-4 h-4" />, onSelect: () => openNewFolder(null) },
            'separator',
            { id: 'open', label: 'Open file…', icon: <FolderOpen className="w-4 h-4" />, onSelect: onImportFile },
        ];
    })();

    // ── Actions ─────────────────────────────────────────────────────────────
    const answerInvitation = async (invitation: ProjectInvitation, accept: boolean) => {
        if (!onAnswerProjectInvitation || answering) return;
        setAnswering(invitation.id);
        try {
            await onAnswerProjectInvitation(invitation.id, accept);
            if (accept) {
                toast.success('Project added', `“${invitation.projectName}” is now under Shared with me.`);
                setLocation({ view: 'shared', folderId: invitation.projectId });
            } else {
                toast.success('Invitation declined', `You will not see “${invitation.projectName}”.`);
            }
        } catch (e) {
            toast.error('Could not answer the invitation', errorText(e, 'Please try again.'));
        } finally {
            setAnswering(null);
        }
    };

    /** A copy of a whole project, accepted (made on the server while the row shows how far it is) or declined */
    const answerCopy = async (copy: ProjectCopy, accept: boolean) => {
        if (!onAnswerProjectCopy || answering) return;
        setAnswering(copy.id);
        if (accept) setCopying({ id: copy.id, copy: { ...copy, status: 'copying' } });
        try {
            const result = await onAnswerProjectCopy(copy.id, accept, (latest) => setCopying({ id: copy.id, copy: latest }));
            if (accept) {
                const made = result?.progress;
                const missing = made && made.missingFiles > 0 ? ` ${pluralize(made.missingFiles, 'file')} the sender kept only on their computer could not come.` : '';
                toast.success('Copy added', `“${copy.projectName}” is now in your library.${missing}`);
                if (result?.newProjectId) setLocation({ view: 'library', folderId: result.newProjectId });
            } else {
                toast.success('Copy declined', `You will not get “${copy.projectName}”.`);
            }
        } catch (e) {
            toast.error(accept ? 'The copy could not be made' : 'Could not answer', copyErrorText(e, errorText(e, 'Please try again.')));
        } finally {
            setAnswering(null);
            setCopying(null);
        }
    };

    const toggleStar = async (w: LibraryWorkspace) => {
        try {
            await onToggleFavorite({ id: w.id, isLocal: w.isLocal }, !w.isFavorite);
        } catch (e) {
            toast.error('Could not update star', errorText(e, 'Please try again.'));
        }
    };

    const submitNewFolder = async (name: string) => {
        if (dialog.kind !== 'new-folder') return;
        const folder = await onCreateFolder({
            name,
            kind: dialog.folderKind,
            parentId: dialog.parentId,
            color: dialog.folderKind === 'project' ? newColor : undefined,
        });
        if (dialog.parentId) {
            setExpandedIds(prev => { const n = new Set(prev); n.add(dialog.parentId as string); writeExpandedFolderIds(n); return n; });
            toast.success('Folder created', `“${folder.name}” was added.`);
        } else {
            goToFolder(folder.id);
        }
    };

    const confirmDeleteFolder = async (mode: DeleteFolderMode, onProgress: (p: DeleteFolderProgress) => void) => {
        if (dialog.kind !== 'delete-folder') throw new Error('No folder selected');
        const f = dialog.folder;
        const parentId = f.parentId;
        const scope = getDescendantFolderIds(folders, f.id);
        scope.add(f.id);
        const result = await onDeleteFolder(f.id, mode, onProgress);
        if (location.folderId && scope.has(location.folderId)) goToFolder(parentId);
        const kind = f.kind === 'project' ? 'Project' : 'Folder';
        if (result.failedWorkspaces > 0) {
            toast.warning(`${kind} deleted with problems`, `${pluralize(result.failedWorkspaces, 'workspace')} could not be ${mode === 'all' ? 'deleted' : 'moved'}.`);
        } else if (mode === 'all') {
            toast.success(`${kind} deleted`, result.affectedWorkspaces > 0 ? `${pluralize(result.affectedWorkspaces, 'workspace')} deleted with it.` : undefined);
        } else {
            toast.success(`${kind} deleted`, result.affectedWorkspaces > 0 ? `${pluralize(result.affectedWorkspaces, 'workspace')} moved to ${describeFolder(folders, parentId)}.` : undefined);
        }
        return result;
    };

    // ── Renderers ───────────────────────────────────────────────────────────
    const renderCard = (w: LibraryWorkspace, withLocation = false) => (
        <WorkspaceCard
            key={w.id}
            workspace={w}
            color={colorOf(w)}
            locationLabel={withLocation ? describeLocation(allFolders, w) : undefined}
            onOpen={() => onOpenWorkspace(w.id, w.isLocal)}
            onToggleFavorite={w.isShared ? undefined : () => { void toggleStar(w); }}
            menuItems={workspaceMenu(w)}
            draggable={!w.isShared}
            onDragStart={(e) => startDrag(e, { kind: 'workspace', id: w.id, isLocal: w.isLocal, folderId: w.folderId, name: w.name })}
            onDragEnd={endDrag}
            dragging={draggingId === w.id}
        />
    );

    const renderFolderTile = (f: WorkspaceFolder) => (
        <FolderTile
            key={f.id}
            folder={f}
            color={getFolderPath(folders, f.id)[0]?.color}
            workspaceCount={counts.get(f.id) || 0}
            folderCount={getDescendantFolderIds(folders, f.id).size}
            totalCost={costs.get(f.id)}
            previews={previewsFor(f.id)}
            editedAt={editedAtIn(f.id)}
            onOpen={() => goToFolder(f.id)}
            menuItems={folderMenu(f)}
            drop={dropProps(f.id)}
            draggable={f.kind === 'folder'}
            onDragStart={(e) => startDrag(e, { kind: 'folder', id: f.id, name: f.name })}
            onDragEnd={endDrag}
            dragging={draggingId === f.id}
        />
    );

    const sortControl = (
        <label className="ns-t-small flex items-center gap-2">
            <span className="hidden sm:inline">Sort</span>
            <select
                aria-label="Sort workspaces"
                className="ns-field w-auto font-medium"
                value={sort}
                onChange={(e) => setSort(e.target.value as LibrarySort)}
            >
                <option value="edited">Last edited</option>
                <option value="name">Name</option>
                <option value="created">Created</option>
            </select>
        </label>
    );

    const newWorkspaceButton = (folderId: string | null, size: 'sm' | 'md' = 'sm', ghost = true) => (
        <button type="button" className={`ns-btn ${ghost ? 'ns-btn-ghost' : 'ns-btn-primary'} ${size === 'sm' ? 'ns-btn-sm' : ''}`} onClick={() => openNewWorkspace(folderId)}>
            <Plus className="w-4 h-4" aria-hidden="true" />New workspace
        </button>
    );

    // The pictures first: the workspaces opened last, as tiles
    const renderContinueRail = () => recent.length === 0 ? null : (
        <section aria-labelledby="continue-heading" className="mb-8">
            <SectionHeader
                id="continue-heading"
                title="Continue where you left off"
                action={<button type="button" className="ns-btn ns-btn-xs ns-btn-quiet ns-btn-flat whitespace-nowrap" onClick={() => setLocation({ view: 'recent', folderId: null })}>All recent<ChevronRight className="w-3.5 h-3.5" aria-hidden="true" /></button>}
            />
            <div className="ns-lib-grid-continue" role="list">
                {recent.slice(0, 5).map(w => renderCard(w))}
            </div>
        </section>
    );

    /** Past eight projects: one row each, the first eight until Show all */
    const renderProjectTable = () => {
        const rows = showAllProjects ? projects : projects.slice(0, 8);
        return (
            <div className="ns-lib-table-wrap">
                <table className="ns-lib-table">
                    <thead>
                        <tr><th scope="col">Project</th><th scope="col">Inside</th><th scope="col">Spent</th><th scope="col">People</th><th scope="col">Edited</th><th scope="col"><span className="sr-only">Actions</span></th></tr>
                    </thead>
                    <tbody>
                        {rows.map(p => {
                            const edited = editedAtIn(p.id);
                            const kids = getDescendantFolderIds(folders, p.id).size;
                            const people = p.sharedUserIds?.length ?? 0;
                            const drop = dropProps(p.id);
                            return (
                                <tr key={p.id} className={drop.active ? 'ns-lib-drop' : ''} onDragOver={drop.onDragOver} onDragLeave={drop.onDragLeave} onDrop={drop.onDrop} data-testid={`project-row-${p.id}`}>
                                    <td>
                                        <button type="button" className="flex items-center gap-2.5 min-w-0 font-semibold text-ink rounded-md" onClick={() => goToFolder(p.id)} aria-label={`Open project ${p.name}`}>
                                            <ProjectTile name={p.name} color={p.color} />
                                            <span className="truncate">{p.name}</span>
                                        </button>
                                    </td>
                                    <td className="whitespace-nowrap">{pluralize(counts.get(p.id) || 0, 'workspace')}{kids > 0 && ` · ${pluralize(kids, 'folder')}`}</td>
                                    <td className="tabular-nums whitespace-nowrap">{(costs.get(p.id) || 0) > 0 ? formatCost(costs.get(p.id) || 0) : '—'}</td>
                                    <td>{people > 0 ? <PeopleBadge count={people} /> : 'Only you'}</td>
                                    <td className="whitespace-nowrap">{edited ? formatRelativeTime(edited) : '—'}</td>
                                    <td className="text-right"><LibraryMenu label={`More actions for ${p.name}`} items={folderMenu(p)} triggerClassName="ns-icon-btn ns-icon-btn-sm" /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {projects.length > 8 && (
                    <div className="ns-lib-table-foot">
                        <span>Showing {rows.length} of {projects.length}</span>
                        <button type="button" className="ns-btn ns-btn-sm ns-btn-flat ns-btn-primary" onClick={() => setShowAllProjects(v => !v)} aria-expanded={showAllProjects}>
                            {showAllProjects ? 'Show fewer' : `Show all ${projects.length}`}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderProjectCard = (p: WorkspaceFolder) => (
        <ProjectCard
            key={p.id}
            project={p}
            workspaceCount={counts.get(p.id) || 0}
            folderCount={getDescendantFolderIds(folders, p.id).size}
            totalCost={costs.get(p.id)}
            childFolders={childrenMap.get(p.id) || []}
            previews={previewsFor(p.id)}
            editedAt={editedAtIn(p.id)}
            onOpen={() => goToFolder(p.id)}
            menuItems={folderMenu(p)}
            drop={dropProps(p.id)}
        />
    );

    const renderRoot = () => {
        if (isLoading) {
            return (
                <>
                    <div className="ns-skeleton h-8 w-48 mb-2" />
                    <div className="ns-skeleton h-4 w-64 mb-8" />
                    <SkeletonGrid count={8} />
                </>
            );
        }
        if (isFirstRun) {
            return (
                <section className="ns-tile ns-tile-lavender p-8 sm:p-12 relative overflow-hidden" aria-labelledby="first-run-title">
                    <span className="ns-t-label text-ink/70 mb-3">Welcome, {user.name.split(' ')[0]}</span>
                    <h1 id="first-run-title" className="text-3xl sm:text-4xl font-bold tracking-tight max-w-lg">Start your first workspace</h1>
                    <p className="text-base text-ink/80 max-w-md mt-3">A workspace is an infinite canvas for images, video and text. Projects and folders keep them organised as your library grows.</p>
                    <div className="flex flex-wrap gap-3 mt-7">
                        {newWorkspaceButton(null, 'md', false)}
                        <button type="button" className="ns-btn ns-btn-ghost border-ink/20" onClick={() => openNewFolder(null)}><FolderKanban className="w-4 h-4" aria-hidden="true" />New project</button>
                        <button type="button" className="ns-btn ns-btn-ghost border-ink/20" onClick={onImportFile}><FolderOpen className="w-4 h-4" aria-hidden="true" />Open file</button>
                    </div>
                </section>
            );
        }
        const unfiledDrop = dropProps(null);
        return (
            <>
                <div className="ns-view-head mb-6">
                    <h1 className="ns-t-page">Your library</h1>
                    <p className="text-sm text-ink-soft mt-1">
                        {pluralize(owned.length, 'workspace')}
                        {projects.length > 0 && ` · ${pluralize(projects.length, 'project')}`}
                        {sharedCount > 0 && ` · ${sharedCount} shared with you`}
                    </p>
                </div>

                {renderContinueRail()}

                {(pinnedProjects.length > 0 || pinnedWorkspaces.length > 0) && (
                    <section aria-labelledby="pinned-heading" className="mb-10">
                        <SectionHeader id="pinned-heading" title="Pinned" count={pinnedProjects.length + pinnedWorkspaces.length} />
                        {pinnedProjects.length > 0 && (
                            <div className={`ns-lib-grid-projects ${pinnedWorkspaces.length > 0 ? 'mb-4' : ''}`} role="list">
                                {pinnedProjects.map(p => renderProjectCard(p))}
                            </div>
                        )}
                        {pinnedWorkspaces.length > 0 && (
                            <div className="ns-lib-grid-continue" role="list">{pinnedWorkspaces.map(w => renderCard(w, true))}</div>
                        )}
                    </section>
                )}

                <section aria-labelledby="projects-heading" className="mb-10">
                    <SectionHeader
                        id="projects-heading"
                        title="Projects"
                        count={projects.length}
                        action={<button type="button" className="ns-btn ns-btn-ghost ns-btn-sm" onClick={() => openNewFolder(null)}><FolderKanban className="w-4 h-4" aria-hidden="true" />New project</button>}
                    />
                    {projects.length === 0 ? (
                        <EmptyTile
                            icon={<FolderKanban />}
                            title="No projects yet"
                            body="Projects group workspaces and folders, like a client, a film or a campaign. Drag workspaces onto a project to file them."
                            action={<button type="button" className="ns-btn ns-btn-primary ns-btn-sm" onClick={() => openNewFolder(null)}><Plus className="w-4 h-4" aria-hidden="true" />New project</button>}
                        />
                    ) : projects.length > 8 ? renderProjectTable() : (
                        <div className="ns-lib-grid-projects" role="list">
                            {projects.map(p => renderProjectCard(p))}
                        </div>
                    )}
                </section>

                {orphanFolders.length > 0 && (
                    <section aria-labelledby="orphans-heading" className="mb-10">
                        <SectionHeader id="orphans-heading" title="Folders without a project" count={orphanFolders.length} />
                        <p className="text-sm text-ink-soft -mt-1 mb-3">Their project was deleted. Move each one into a project, or delete it.</p>
                        <div className="ns-lib-grid-folders" role="list">{orphanFolders.map(renderFolderTile)}</div>
                    </section>
                )}

                <section
                    aria-labelledby="unfiled-heading"
                    className={`rounded-3xl -m-2 p-2 transition-colors ${unfiledDrop.active ? 'ns-lib-drop' : ''}`}
                    onDragOver={unfiledDrop.onDragOver}
                    onDragLeave={unfiledDrop.onDragLeave}
                    onDrop={unfiledDrop.onDrop}
                >
                    <SectionHeader
                        id="unfiled-heading"
                        title={projects.length > 0 ? 'Not in a project' : 'Workspaces'}
                        count={unfiled.length}
                        action={<>{unfiled.length > 1 && sortControl}{newWorkspaceButton(null)}</>}
                    />
                    {unfiled.length === 0 ? (
                        <EmptyTile
                            icon={<Inbox />}
                            title={projects.length > 0 ? 'Everything is filed' : 'No workspaces yet'}
                            body={projects.length > 0 ? 'Workspaces that are not inside a project show up here.' : 'Create a workspace to start a canvas.'}
                            action={newWorkspaceButton(null, 'sm', false)}
                        />
                    ) : (
                        <div className="ns-lib-grid-cards" role="list">
                            {sortWorkspaces(unfiled, sort).map(w => renderCard(w))}
                        </div>
                    )}
                </section>
            </>
        );
    };

    const renderFolder = (folder: WorkspaceFolder) => {
        const kids = childrenMap.get(folder.id) || [];
        const items = workspacesIn(folder.id);
        const folderReason = newFolderReason(folder.id);
        const depth = currentPath.length;
        return (
            <>
                <div className="ns-view-head mb-7">
                <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-0.5 -ml-2 mb-3">
                    {(() => {
                        const rootDrop = dropProps(null);
                        return (
                            <button type="button" className={`ns-lib-crumb ${rootDrop.active ? 'ns-lib-drop' : ''}`} onClick={() => goToFolder(null)} onDragOver={rootDrop.onDragOver} onDragLeave={rootDrop.onDragLeave} onDrop={rootDrop.onDrop}>
                                Library
                            </button>
                        );
                    })()}
                    {currentPath.map((f, i) => {
                        const last = i === currentPath.length - 1;
                        const crumbDrop = dropProps(f.id);
                        return (
                            <React.Fragment key={f.id}>
                                <ChevronRight className="w-3.5 h-3.5 text-ink-faint flex-none" aria-hidden="true" />
                                <button
                                    type="button"
                                    className={`ns-lib-crumb ${crumbDrop.active ? 'ns-lib-drop' : ''}`}
                                    aria-current={last ? 'page' : undefined}
                                    onClick={() => { if (!last) goToFolder(f.id); }}
                                    onDragOver={last ? undefined : crumbDrop.onDragOver}
                                    onDragLeave={last ? undefined : crumbDrop.onDragLeave}
                                    onDrop={last ? undefined : crumbDrop.onDrop}
                                    title={f.name}
                                >
                                    <span className="truncate">{f.name}</span>
                                </button>
                            </React.Fragment>
                        );
                    })}
                </nav>

                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                        {folder.kind === 'project' ? (
                            <ProjectTile name={folder.name} color={folder.color} size="lg" />
                        ) : (
                            <span className="ns-mark ns-mark-lavender" aria-hidden="true"><FolderOpen /></span>
                        )}
                        <div className="min-w-0">
                            <div className="flex items-center gap-1 min-w-0">
                                <h1 className="ns-t-page truncate" title={folder.name}>{folder.name}</h1>
                                {isPinned(folder.id) && <Pin className="w-4 h-4 text-ink-faint flex-none" aria-label="Pinned" />}
                                <LibraryMenu label={`Actions for ${folder.name}`} items={folderMenu(folder)} triggerClassName="ns-icon-btn" />
                            </div>
                            <p className="text-sm text-ink-soft mt-0.5 flex flex-wrap items-center gap-x-1.5">
                                <span>
                                    {folder.kind === 'project' ? 'Project' : depth > 1 ? `Folder in ${currentProject?.name}` : 'Folder without a project'}
                                    {' · '}{pluralize(counts.get(folder.id) || 0, 'workspace')}
                                    {kids.length > 0 && ` · ${pluralize(kids.length, 'folder')}`}
                                    {(costs.get(folder.id) || 0) > 0 && ` · ${formatCost(costs.get(folder.id) || 0)}`}
                                </span>
                                {(currentProject?.sharedUserIds?.length ?? 0) > 0 && (
                                    <span className="inline-flex items-center gap-1.5" data-project-shared-count={currentProject?.sharedUserIds?.length}>
                                        <PeopleBadge count={currentProject?.sharedUserIds?.length ?? 0} />
                                        Shared with {pluralize(currentProject?.sharedUserIds?.length ?? 0, 'person', 'people')}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-none">
                        {folder.kind === 'project' && canShareProjects && (
                            <button type="button" className="ns-btn ns-btn-ghost ns-btn-sm" onClick={() => setSharingProject(folder)}>
                                <Users className="w-4 h-4" aria-hidden="true" />Share
                            </button>
                        )}
                        <button type="button" className="ns-btn ns-btn-ghost ns-btn-sm" onClick={() => openNewFolder(folder.id)} disabled={!!folderReason} title={folderReason}>
                            <FolderPlus className="w-4 h-4" aria-hidden="true" />New folder
                        </button>
                        {newWorkspaceButton(folder.id, 'sm', false)}
                    </div>
                </div>
                </div>

                {kids.length > 0 && (
                    <section aria-labelledby="folders-heading" className="mb-8">
                        <SectionHeader id="folders-heading" title="Folders" count={kids.length} />
                        <div className="ns-lib-grid-folders" role="list">{kids.map(renderFolderTile)}</div>
                    </section>
                )}

                <section aria-labelledby="workspaces-heading">
                    <SectionHeader id="workspaces-heading" title="Workspaces" count={items.length} action={items.length > 1 ? sortControl : undefined} />
                    {items.length === 0 ? (
                        // One button: "New folder" is in the head of this page, right above
                        <EmptyTile
                            icon={<Sparkles />}
                            title="Nothing here yet"
                            body={kids.length > 0 ? 'Workspaces in the folders above are counted in their own folder.' : 'Create a workspace here, or drag existing ones onto this folder in the sidebar.'}
                            action={newWorkspaceButton(folder.id, 'sm', false)}
                        />
                    ) : (
                        <div className="ns-lib-grid-cards" role="list">{sortWorkspaces(items, sort).map(w => renderCard(w))}</div>
                    )}
                </section>
            </>
        );
    };

    const renderList = (title: string, items: LibraryWorkspace[], empty: { title: string; body: string }, options: { presorted?: boolean; showSort?: boolean } = {}) => (
        <>
            <div className="ns-view-head flex flex-wrap items-end justify-between gap-3 mb-6">
                <div>
                    <h1 className="ns-t-page">{title}</h1>
                    <p className="text-sm text-ink-soft mt-1">{pluralize(items.length, 'workspace')}</p>
                </div>
                {options.showSort && items.length > 1 && sortControl}
            </div>
            {isLoading ? <SkeletonGrid count={6} /> : items.length === 0 ? (
                <EmptyTile icon={<Sparkles />} title={empty.title} body={empty.body} action={<button type="button" className="ns-btn ns-btn-ghost ns-btn-sm" onClick={() => goToFolder(null)}>Browse library</button>} />
            ) : (
                <div className="ns-lib-grid-cards" role="list">
                    {(options.presorted ? items : sortWorkspaces(items, sort)).map(w => renderCard(w, true))}
                </div>
            )}
        </>
    );

    const sharedFolderTile = (f: WorkspaceFolder) => (
        <FolderTile
            key={f.id}
            folder={f}
            color={getFolderPath(sharedFolders, f.id)[0]?.color}
            workspaceCount={sharedCounts.get(f.id) || 0}
            folderCount={getDescendantFolderIds(sharedFolders, f.id).size}
            totalCost={sharedCosts.get(f.id)}
            onOpen={() => setLocation({ view: 'shared', folderId: f.id })}
            menuItems={[{ id: 'open', label: 'Open', icon: <FolderOpen className="w-4 h-4" />, onSelect: () => setLocation({ view: 'shared', folderId: f.id }) }]}
        />
    );

    const renderShared = () => (
        <>
            {copiesForMe.length > 0 && (
                <section aria-labelledby="copies-heading" className="mb-10">
                    <SectionHeader id="copies-heading" title="Copies for you" count={copiesForMe.length} />
                    <ul className="flex flex-col gap-2">
                        {copiesForMe.map(copy => {
                            const live = copying?.id === copy.id ? copying.copy : copy;
                            const progress = live.progress;
                            const making = copying?.id === copy.id || live.status === 'copying';
                            return (
                                <li key={copy.id} className="ns-lib-card flex flex-row flex-wrap items-center gap-3 px-4 py-3" data-project-copy={copy.id} data-copy-status={making ? 'copying' : live.status}>
                                    <span className="ns-mark" data-tone="lavender" aria-hidden="true"><Copy /></span>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold truncate">{copy.projectName}</p>
                                        <p className="text-sm text-ink-soft">
                                            {making
                                                ? (progress ? `Copying: ${progress.copiedWorkspaces} of ${progress.totalWorkspaces} workspaces, ${pluralize(progress.copiedFiles, 'file')} so far` : 'Copying…')
                                                : `${copy.fromName} sent you your own copy of this project · ${pluralize(copy.workspaceCount, 'workspace')}${copy.folderCount > 0 ? `, ${pluralize(copy.folderCount, 'folder')}` : ''}`}
                                        </p>
                                        {making && progress && progress.totalWorkspaces > 0 && (
                                            <div className="ns-meter mt-2 max-w-xs" role="progressbar" aria-valuemin={0} aria-valuemax={progress.totalWorkspaces} aria-valuenow={progress.copiedWorkspaces} aria-label="Workspaces copied">
                                                <div style={{ width: `${Math.round((progress.copiedWorkspaces / progress.totalWorkspaces) * 100)}%` }} />
                                            </div>
                                        )}
                                    </div>
                                    {making ? (
                                        <Loader2 className="w-4 h-4 animate-spin text-ink-faint flex-none" aria-hidden="true" />
                                    ) : (
                                        <div className="flex items-center gap-2 flex-none">
                                            <button type="button" className="ns-btn ns-btn-primary ns-btn-sm" disabled={!!answering} onClick={() => { void answerCopy(copy, true); }}>
                                                Accept
                                            </button>
                                            <button type="button" className="ns-btn ns-btn-ghost ns-btn-sm" disabled={!!answering} onClick={() => { void answerCopy(copy, false); }}>
                                                Decline
                                            </button>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}
            {invitations.length > 0 && (
                <section aria-labelledby="invitations-heading" className="mb-10">
                    <SectionHeader id="invitations-heading" title="Waiting for your answer" count={invitations.length} />
                    <ul className="flex flex-col gap-2">
                        {invitations.map(inv => (
                            <li key={inv.id} className="ns-lib-card flex flex-row flex-wrap items-center gap-3 px-4 py-3" data-project-invitation={inv.id}>
                                <span className="ns-mark" data-tone="lavender" aria-hidden="true"><FolderKanban /></span>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold truncate">{inv.projectName}</p>
                                    <p className="text-sm text-ink-soft">{inv.inviterName} wants to share this project with you · {inv.permission === 'edit' ? 'can edit' : 'can view'}</p>
                                </div>
                                <div className="flex items-center gap-2 flex-none">
                                    <button type="button" className="ns-btn ns-btn-primary ns-btn-sm" disabled={!!answering} onClick={() => { void answerInvitation(inv, true); }}>
                                        Accept
                                    </button>
                                    <button type="button" className="ns-btn ns-btn-ghost ns-btn-sm" disabled={!!answering} onClick={() => { void answerInvitation(inv, false); }}>
                                        Decline
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
            <div className="ns-view-head flex flex-wrap items-end justify-between gap-3 mb-6">
                <div>
                    <h1 className="ns-t-page">Shared with me</h1>
                    <p className="text-sm text-ink-soft mt-1">
                        {sharedProjects.length > 0 && `${pluralize(sharedProjects.length, 'project')} · `}{pluralize(sharedDirect.length, 'workspace')}
                    </p>
                </div>
                {sharedDirect.length > 1 && sortControl}
            </div>
            {sharedProjects.length > 0 && (
                <section aria-labelledby="shared-projects-heading" className="mb-10">
                    <SectionHeader id="shared-projects-heading" title="Projects you are in" count={sharedProjects.length} action={<span className="ns-t-small">These also sit in your sidebar, with the people on them</span>} />
                    <div className="ns-lib-grid-projects" role="list">
                        {sharedProjects.map(p => (
                            <ProjectCard
                                key={p.id}
                                project={p}
                                workspaceCount={sharedCounts.get(p.id) || 0}
                                folderCount={getDescendantFolderIds(sharedFolders, p.id).size}
                                totalCost={sharedCosts.get(p.id)}
                                childFolders={sharedChildren.get(p.id) || []}
                                previews={collectWorkspaceIdsInFolder(sharedFolders, sharedInProjects, p.id).filter(w => !!w.thumbnail).sort((a, b) => b.lastSaved - a.lastSaved).slice(0, 4).map(w => w.thumbnail as string)}
                                editedAt={collectWorkspaceIdsInFolder(sharedFolders, sharedInProjects, p.id).reduce<number | undefined>((latest, w) => (w.lastSaved > (latest ?? 0) ? w.lastSaved : latest), undefined)}
                                onOpen={() => setLocation({ view: 'shared', folderId: p.id })}
                                menuItems={sharedProjectMenu(p)}
                            />
                        ))}
                    </div>
                </section>
            )}
            {/* What people shared one by one, by person */}
            {isLoading ? <SkeletonGrid count={6} /> : sharedDirect.length === 0 ? (
                <section aria-labelledby="shared-workspaces-heading">
                    {sharedProjects.length > 0 && <SectionHeader id="shared-workspaces-heading" title="Workspaces" count={0} />}
                    <EmptyTile
                        icon={<Users />}
                        title={sharedProjects.length > 0 ? 'No workspaces on their own' : 'Nothing shared yet'}
                        body={sharedProjects.length > 0 ? 'Workspaces shared with you one by one show up here; those of a shared project are in it.' : 'Projects and workspaces other people share with you show up here.'}
                    />
                </section>
            ) : sharedByPerson.filter(g => g.workspaces.length > 0).map(group => (
                <section key={group.by} aria-label={`From ${group.by}`} className="mb-8">
                    <div className="flex items-center gap-2.5 mb-3 min-w-0">
                        <span className="ns-avatar [--size:26px]" aria-hidden="true">{group.by.charAt(0).toUpperCase()}</span>
                        <h2 className="text-[15px] font-bold truncate">{group.by}</h2>
                        <span className="ns-t-small">{pluralize(group.workspaces.length, 'workspace')}</span>
                    </div>
                    <div className="ns-lib-grid-cards" role="list">{sortWorkspaces(group.workspaces, sort).map(w => renderCard(w, true))}</div>
                </section>
            ))}
        </>
    );

    const renderSharedFolder = (folder: WorkspaceFolder) => {
        const path = getFolderPath(sharedFolders, folder.id);
        const project = path[0] ?? folder;
        const kids = sharedChildren.get(folder.id) || [];
        const items = sharedInProjects.filter(w => w.folderId === folder.id);
        return (
            <>
                <div className="ns-view-head mb-7">
                <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-0.5 -ml-2 mb-3">
                    <button type="button" className="ns-lib-crumb" onClick={() => setLocation({ view: 'shared', folderId: null })}>Shared with me</button>
                    {path.map((f, i) => {
                        const last = i === path.length - 1;
                        return (
                            <React.Fragment key={f.id}>
                                <ChevronRight className="w-3.5 h-3.5 text-ink-faint flex-none" aria-hidden="true" />
                                <button type="button" className="ns-lib-crumb" aria-current={last ? 'page' : undefined} onClick={() => { if (!last) setLocation({ view: 'shared', folderId: f.id }); }} title={f.name}>
                                    <span className="truncate">{f.name}</span>
                                </button>
                            </React.Fragment>
                        );
                    })}
                </nav>

                <div className="flex flex-wrap items-start justify-between gap-4" data-shared-project={project.id}>
                    <div className="flex items-start gap-3 min-w-0">
                        <span className="ns-mark" data-tone={colorTone(project.color)} aria-hidden="true">
                            {folder.kind === 'project' ? <FolderKanban /> : <FolderOpen />}
                        </span>
                        <div className="min-w-0">
                            <h1 className="ns-t-page truncate" title={folder.name}>{folder.name}</h1>
                            <p className="text-sm text-ink-soft mt-0.5">
                                {folder.kind === 'project' ? 'Project' : `Folder in ${project.name}`}
                                {project.sharedBy && ` · Shared by ${project.sharedBy}`}
                                {' · '}{project.sharedPermission === 'edit' ? 'You can edit' : 'You can view'}
                                {' · '}{pluralize(sharedCounts.get(folder.id) || 0, 'workspace')}
                            </p>
                        </div>
                    </div>
                    {folder.kind === 'project' && onLeaveSharedProject && (
                        <button type="button" className="ns-btn ns-btn-ghost ns-btn-sm flex-none" onClick={() => setDialog({ kind: 'leave-project', project: folder })}>
                            <UserMinus className="w-4 h-4" aria-hidden="true" />Leave project
                        </button>
                    )}
                </div>
                </div>

                {kids.length > 0 && (
                    <section aria-labelledby="shared-folders-heading" className="mb-8">
                        <SectionHeader id="shared-folders-heading" title="Folders" count={kids.length} />
                        <div className="ns-lib-grid-folders" role="list">{kids.map(sharedFolderTile)}</div>
                    </section>
                )}

                <section aria-labelledby="shared-items-heading">
                    <SectionHeader id="shared-items-heading" title="Workspaces" count={items.length} action={items.length > 1 ? sortControl : undefined} />
                    {items.length === 0 ? (
                        <EmptyTile icon={<Sparkles />} title="Nothing here yet" body={kids.length > 0 ? 'Workspaces in the folders above are counted in their own folder.' : 'Workspaces the owner adds here show up for you too.'} />
                    ) : (
                        <div className="ns-lib-grid-cards" role="list">{sortWorkspaces(items, sort).map(w => renderCard(w))}</div>
                    )}
                </section>
            </>
        );
    };

    const renderSearch = () => {
        const q = search.trim();
        const folderHits = folders.filter(f => matchesQuery(f.name, q));
        const workspaceHits = workspaces.filter(w => matchesQuery(w.name, q));
        return (
            <>
                <div className="ns-view-head flex items-end justify-between gap-3 mb-6">
                    <div>
                        <h1 className="ns-t-page">Results for “{q}”</h1>
                        <p className="text-sm text-ink-soft mt-1">{pluralize(workspaceHits.length, 'workspace')}{folderHits.length > 0 && ` · ${pluralize(folderHits.length, 'folder')}`}</p>
                    </div>
                    <button type="button" className="ns-btn ns-btn-ghost ns-btn-sm" onClick={() => { setSearch(''); searchRef.current?.focus(); }}><X className="w-4 h-4" aria-hidden="true" />Clear</button>
                </div>
                {folderHits.length > 0 && (
                    <section aria-labelledby="search-folders" className="mb-8">
                        <SectionHeader id="search-folders" title="Folders" count={folderHits.length} />
                        <div className="ns-lib-grid-folders" role="list">{folderHits.map(renderFolderTile)}</div>
                    </section>
                )}
                <section aria-labelledby="search-workspaces">
                    <SectionHeader id="search-workspaces" title="Workspaces" count={workspaceHits.length} />
                    {workspaceHits.length === 0 ? (
                        <EmptyTile icon={<Search />} title="No matches" body="Try a shorter name, or browse the library." />
                    ) : (
                        <div className="ns-lib-grid-cards" role="list">{workspaceHits.map(w => renderCard(w, true))}</div>
                    )}
                </section>
            </>
        );
    };

    const renderLoadError = () => (!loadError || isLoading) ? null : (
        // (An error, not an empty state: it keeps both ways out, "Try again" and "Reload page")
        <div role="alert" className={`${EMPTY_TILE} mb-8 items-start text-left sm:items-center sm:text-center`}>
            <span className="ns-mark ns-mark-lg" data-tone="danger" aria-hidden="true"><AlertTriangle /></span>
            <p className="ns-empty-title">Your library could not be loaded</p>
            <p className="ns-empty-text max-w-lg break-words">{loadError}</p>
            <p className="ns-empty-text max-w-lg">Nothing has been deleted. Whatever did load is shown below.</p>
            <div className="flex flex-wrap gap-2 mt-3">
                <button type="button" className="ns-btn ns-btn-primary ns-btn-sm" onClick={() => { void onRefresh(); }}>Try again</button>
                <button type="button" className="ns-btn ns-btn-ghost ns-btn-sm" onClick={() => window.location.reload()}>Reload page</button>
            </div>
        </div>
    );

    const renderMain = () => {
        if (search.trim()) return renderSearch();
        switch (location.view) {
            case 'recent':
                return renderList('Recent', recentWorkspaces(workspaces, 50), { title: 'Nothing opened yet', body: 'Workspaces you open on this device show up here.' }, { presorted: true });
            case 'starred':
                return renderList('Starred', starred, { title: 'No starred workspaces', body: 'Star a workspace to keep it one click away.' }, { showSort: true });
            case 'shared':
                return sharedCurrent ? renderSharedFolder(sharedCurrent) : renderShared();
            default:
                return currentFolder ? renderFolder(currentFolder) : renderRoot();
        }
    };

    // ── Sidebar ─────────────────────────────────────────────────────────────
    const navRow = (label: string, icon: React.ReactNode, active: boolean, onClick: () => void, count?: number) => (
        <li>
            <button type="button" className="ns-lib-row" aria-current={active ? 'page' : undefined} onClick={onClick}>
                <span className="w-5 h-5 grid place-items-center text-ink-soft flex-none" aria-hidden="true">{icon}</span>
                <span className="truncate">{label}</span>
                {typeof count === 'number' && <span className="ns-lib-count">{count}</span>}
            </button>
        </li>
    );

    // Inside a project the sidebar narrows to that project (Back returns to all projects);
    // everywhere else it lists the smart views and every project.
    const inProject = location.view === 'library' && !search && !!currentProject;
    // Inside a project someone shared with this person, the sidebar narrows to it the same way
    const sharedProjectRoot = sharedCurrent ? (getFolderPath(sharedFolders, sharedCurrent.id)[0] ?? sharedCurrent) : null;
    const inSharedProject = location.view === 'shared' && !search && !!sharedProjectRoot;
    const sidebar = inSharedProject && sharedProjectRoot ? (
        <ProjectPanel
            context={{ kind: 'project', project: sharedProjectRoot }}
            folders={sharedFolders}
            workspaces={sharedInProjects}
            selectedId={location.folderId}
            onSelect={(id) => setLocation({ view: 'shared', folderId: id ?? sharedProjectRoot.id })}
            expandedIds={expandedIds}
            onToggleExpand={toggleExpand}
            onOpenWorkspace={(id) => { const w = sharedInProjects.find(x => x.id === id); if (w) onOpenWorkspace(w.id, w.isLocal); }}
            onBack={() => setLocation({ view: 'shared', folderId: null })}
            backLabel="Shared with me"
            renderMenu={(f) => (f.kind === 'project' ? <LibraryMenu label={`Actions for ${f.name}`} items={sharedProjectMenu(f)} triggerClassName="ns-icon-btn ns-icon-btn-sm" triggerTabIndex={-1} /> : null)}
            renderWorkspaceMenu={(w) => <LibraryMenu label={`More actions for ${w.name}`} items={workspaceMenu(w)} triggerClassName="ns-icon-btn ns-icon-btn-sm" triggerTabIndex={-1} />}
        >
            <p className="ns-t-meta px-2 mt-3" data-shared-project-access>
                {sharedProjectRoot.sharedBy ? `Shared by ${sharedProjectRoot.sharedBy} · ` : ''}{sharedProjectRoot.sharedPermission === 'edit' ? 'You can edit' : 'You can view'}
            </p>
        </ProjectPanel>
    ) : inProject && currentProject ? (
        <ProjectPanel
            context={{ kind: 'project', project: currentProject }}
            folders={folders}
            workspaces={workspaces}
            selectedId={location.folderId}
            onSelect={(id) => goToFolder(id ?? currentProject.id)}
            expandedIds={expandedIds}
            onToggleExpand={toggleExpand}
            onOpenWorkspace={(id) => { const w = workspaces.find(x => x.id === id); if (w) onOpenWorkspace(w.id, w.isLocal); }}
            onWorkspaceDragStart={(e, w) => startDrag(e, { kind: 'workspace', id: w.id, isLocal: w.isLocal, folderId: w.folderId, name: w.name })}
            onBack={() => goToFolder(null)}
            backLabel="All projects"
            renderMenu={(f) => <LibraryMenu label={`Actions for ${f.name}`} items={folderMenu(f)} triggerClassName="ns-icon-btn ns-icon-btn-sm" triggerTabIndex={-1} />}
            drop={treeDrop}
        />
    ) : (
        <nav aria-label="Library" className="flex flex-col">
            <ul className="flex flex-col gap-0.5">
                {navRow('All workspaces', <LayoutGrid className="w-4 h-4" />, location.view === 'library' && !location.folderId && !search, () => goToFolder(null), isLoading ? undefined : owned.length)}
                {/* While search results are shown, no place is the current one */}
                {navRow('Recent', <Clock className="w-4 h-4" />, location.view === 'recent' && !search, () => setLocation({ view: 'recent', folderId: null }))}
                {navRow('Starred', <Star className="w-4 h-4" />, location.view === 'starred' && !search, () => setLocation({ view: 'starred', folderId: null }), isLoading ? undefined : starred.length)}
                {sharedCount + invitations.length + copiesForMe.length > 0 && navRow('Shared with me', <Users className="w-4 h-4" />, location.view === 'shared' && !location.folderId && !search, () => setLocation({ view: 'shared', folderId: null }), sharedCount + invitations.length + copiesForMe.length)}
            </ul>

            {/* What this person pinned: a project, a folder or a workspace, one row each */}
            {(pinnedProjects.length > 0 || pinnedFolders.length > 0 || pinnedWorkspaces.length > 0) && (
                <section aria-label="Pinned" className="mt-6">
                    <div className="ns-lib-side-label">
                        <span className="ns-t-label">Pinned</span>
                        <Pin className="w-3 h-3 text-ink-faint" aria-hidden="true" />
                    </div>
                    <ul className="flex flex-col gap-0.5">
                        {pinnedProjects.map(p => (
                            <li key={p.id}>
                                <button type="button" className="ns-lib-row" aria-current={location.view === 'library' && location.folderId === p.id && !search ? 'page' : undefined} onClick={() => goToFolder(p.id)} title={`Open the project ${p.name}`}>
                                    <ProjectTile name={p.name} color={p.color} size="sm" />
                                    <span className="truncate flex-1 min-w-0 font-semibold">{p.name}</span>
                                    <PeopleBadge count={p.sharedUserIds?.length ?? 0} />
                                    <span className="ns-lib-count">{counts.get(p.id) || 0}</span>
                                </button>
                            </li>
                        ))}
                        {pinnedFolders.map(f => (
                            <li key={f.id}>
                                <button type="button" className="ns-lib-row" aria-current={location.view === 'library' && location.folderId === f.id && !search ? 'page' : undefined} onClick={() => goToFolder(f.id)} title={`Open the folder ${f.name}`}>
                                    <FolderOpen className="w-4 h-4 text-ink-soft flex-none" aria-hidden="true" />
                                    <span className="truncate flex-1 min-w-0">{f.name} <span className="text-ink-faint font-normal">· {getFolderPath(folders, f.id)[0]?.name}</span></span>
                                    <span className="ns-lib-count">{counts.get(f.id) || 0}</span>
                                </button>
                            </li>
                        ))}
                        {pinnedWorkspaces.map(w => (
                            <li key={w.id}>
                                <button type="button" className="ns-lib-row" onClick={() => onOpenWorkspace(w.id, w.isLocal)} title={`Open ${w.name}`}>
                                    <WorkspaceThumb thumbnail={w.thumbnail} />
                                    <span className="truncate flex-1 min-w-0">{w.name}{w.isShared && w.sharedBy && <span className="text-ink-faint font-normal"> · {w.sharedBy}</span>}</span>
                                    {w.isShared && <span className="ns-lib-count">{w.sharedPermission === 'edit' ? 'edit' : 'view'}</span>}
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <div className="ns-lib-side-label mt-6">
                <span className="ns-t-label">Projects{projects.length > 0 && <span className="text-ink-faint font-medium tracking-normal normal-case tabular-nums"> · {projects.length}</span>}</span>
                <button type="button" className="ns-icon-btn ns-icon-btn-xs" aria-label="New project" title="New project" onClick={() => openNewFolder(null)}>
                    <Plus aria-hidden="true" />
                </button>
            </div>

            {isLoading ? (
                <div className="flex flex-col gap-2 px-2" aria-hidden="true">
                    <div className="ns-skeleton h-6 w-5/6" /><div className="ns-skeleton h-6 w-2/3" /><div className="ns-skeleton h-6 w-3/4" />
                </div>
            ) : projects.length === 0 ? (
                <p className="text-sm text-ink-soft px-2 leading-relaxed">Group workspaces into projects and folders.</p>
            ) : (
                <>
                    {projects.length > 8 && (
                        <div className="relative mb-2">
                            <Search className="w-3.5 h-3.5 text-ink-soft absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
                            <input
                                type="search"
                                className="ns-field ns-field-sm pl-8 pr-9 rounded-full"
                                placeholder="Filter projects, folders, workspaces"
                                aria-label="Filter projects"
                                value={projectFilter}
                                onChange={(e) => setProjectFilter(e.target.value)}
                            />
                            {projectFilter && (
                                <span className="absolute right-1 inset-y-0 flex items-center">
                                    <button type="button" className="ns-icon-btn ns-icon-btn-xs" aria-label="Clear filter" onClick={() => setProjectFilter('')}>
                                        <X aria-hidden="true" />
                                    </button>
                                </span>
                            )}
                        </div>
                    )}
                    {/* A project unfolds into its folders and then its workspaces; the one that is open is the only one unfolded by default */}
                    <FolderTree
                        variant="outline"
                        folders={folders}
                        counts={counts}
                        costs={costs}
                        selectedId={location.view === 'library' && !search ? location.folderId : null}
                        onSelect={(id) => goToFolder(id)}
                        expandedIds={expandedIds}
                        onToggleExpand={toggleExpand}
                        mode="nav"
                        renderMenu={(f) => <LibraryMenu label={`Actions for ${f.name}`} items={folderMenu(f)} triggerClassName="ns-icon-btn ns-icon-btn-sm" triggerTabIndex={-1} />}
                        renderLeafMenu={(leaf) => { const w = workspaces.find(x => x.id === leaf.id); return w ? <LibraryMenu label={`More actions for ${w.name}`} items={workspaceMenu(w)} triggerClassName="ns-icon-btn ns-icon-btn-sm" triggerTabIndex={-1} /> : null; }}
                        leaves={treeLeaves}
                        onSelectLeaf={(leaf) => { const w = workspaces.find(x => x.id === leaf.id); if (w) onOpenWorkspace(w.id, w.isLocal); }}
                        onLeafDragStart={(e, leaf) => { const w = workspaces.find(x => x.id === leaf.id); if (w) startDrag(e, { kind: 'workspace', id: w.id, isLocal: w.isLocal, folderId: w.folderId, name: w.name }); }}
                        drop={treeDrop}
                        filter={projects.length > 8 ? projectFilter : ''}
                        ariaLabel="Projects"
                    />
                </>
            )}

            {/* Under this person's own projects: the projects others shared with them, with who shared each; what people shared one by one is on the Shared with me page */}
            {(sharedByPerson.length > 0 || invitations.length > 0 || copiesForMe.length > 0) && (
                <section aria-label="Shared with me" className="mt-6">
                    <div className="ns-lib-side-label">
                        <span className="ns-t-label">Shared with me</span>
                        <button type="button" className="ns-t-meta tabular-nums rounded-md" onClick={() => setLocation({ view: 'shared', folderId: null })} title="Everything shared with you">{sharedCount}</button>
                    </div>
                    {invitations.length > 0 && (
                        <ul className="flex flex-col gap-0.5 mb-2.5" aria-label="Waiting for your answer">
                            {invitations.map(inv => (
                                <li key={inv.id} className="flex items-center gap-1 pl-3 pr-1 min-w-0" data-sidebar-invitation={inv.id}>
                                    <span className="w-5 h-5 grid place-items-center text-lavender-deep flex-none" aria-hidden="true"><FolderKanban className="w-4 h-4" /></span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm truncate" title={inv.projectName}>{inv.projectName}</span>
                                        <span className="block ns-t-meta truncate">from {inv.inviterName}</span>
                                    </span>
                                    <button type="button" className="ns-icon-btn ns-icon-btn-sm" aria-label={`Accept ${inv.projectName}`} title="Accept" disabled={!!answering} onClick={() => { void answerInvitation(inv, true); }}>
                                        <Check aria-hidden="true" />
                                    </button>
                                    <button type="button" className="ns-icon-btn ns-icon-btn-sm" aria-label={`Decline ${inv.projectName}`} title="Decline" disabled={!!answering} onClick={() => { void answerInvitation(inv, false); }}>
                                        <X aria-hidden="true" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {copiesForMe.length > 0 && (
                        <ul className="flex flex-col gap-0.5 mb-2.5" aria-label="Copies for you">
                            {copiesForMe.map(copy => (
                                <li key={copy.id} className="flex items-center gap-1 pl-3 pr-1 min-w-0" data-sidebar-copy={copy.id}>
                                    <span className="w-5 h-5 grid place-items-center text-lavender-deep flex-none" aria-hidden="true"><Copy className="w-4 h-4" /></span>
                                    <button type="button" className="min-w-0 flex-1 text-left rounded-md" onClick={() => setLocation({ view: 'shared', folderId: null })} title={`A copy of ${copy.projectName} from ${copy.fromName}`}>
                                        <span className="block text-sm truncate">{copy.projectName}</span>
                                        <span className="block ns-t-meta truncate">a copy, from {copy.fromName}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {/* By person: their projects (with your access), then up to three of the workspaces they shared one by one; the rest are on the page */}
                    <ul className="flex flex-col gap-2">
                        {sharedByPerson.map(group => (
                            <li key={group.by}>
                                <div className="flex items-center gap-2 pl-2 pr-1 mb-0.5 min-w-0">
                                    <span className="ns-avatar [--size:18px]" aria-hidden="true">{group.by.charAt(0).toUpperCase()}</span>
                                    <span className="ns-t-small truncate" title={group.by}>{group.by}</span>
                                </div>
                                {/* Their projects unfold into folders and workspaces, as this person's own do */}
                                {group.projects.length > 0 && (() => {
                                    const projectIds = new Set(group.projects.map(p => p.id));
                                    return (
                                        <FolderTree
                                            variant="outline"
                                            folders={sharedFolders.filter(f => projectIds.has(sharedProjectOf.get(f.id) ?? f.id))}
                                            counts={sharedCounts}
                                            costs={sharedCosts}
                                            selectedId={location.view === 'shared' && !search ? location.folderId : null}
                                            onSelect={(id) => { if (id) setLocation({ view: 'shared', folderId: id }); }}
                                            expandedIds={expandedIds}
                                            onToggleExpand={toggleExpand}
                                            mode="nav"
                                            renderMenu={(f) => (f.kind === 'project' ? <LibraryMenu label={`Actions for ${f.name}`} items={sharedProjectMenu(f)} triggerClassName="ns-icon-btn ns-icon-btn-sm" triggerTabIndex={-1} /> : null)}
                                            renderBadge={(f) => (f.kind === 'project' ? <span className="ns-lib-count" data-shared-project={f.id}>{f.sharedPermission === 'edit' ? 'edit' : 'view'}</span> : null)}
                                            renderLeafMenu={(leaf) => { const w = sharedInProjects.find(x => x.id === leaf.id); return w ? <LibraryMenu label={`More actions for ${w.name}`} items={workspaceMenu(w)} triggerClassName="ns-icon-btn ns-icon-btn-sm" triggerTabIndex={-1} /> : null; }}
                                            leaves={sharedTreeLeaves}
                                            onSelectLeaf={(leaf) => { const w = sharedInProjects.find(x => x.id === leaf.id); if (w) onOpenWorkspace(w.id, w.isLocal); }}
                                            ariaLabel={`Projects ${group.by} shared with you`}
                                        />
                                    );
                                })()}
                                <ul className="flex flex-col gap-0.5">
                                    {group.workspaces.slice(0, 3).map(w => (
                                        <li key={w.id}>
                                            <button type="button" className="ns-lib-row" onClick={() => onOpenWorkspace(w.id, w.isLocal)} title={`Open ${w.name}`} data-shared-workspace={w.id}>
                                                <WorkspaceThumb thumbnail={w.thumbnail} />
                                                <span className="truncate flex-1 min-w-0">{w.name}</span>
                                                {w.sharedPermission === 'view' && <span className="ns-lib-count">view</span>}
                                            </button>
                                        </li>
                                    ))}
                                    {group.workspaces.length > 3 && (
                                        <li>
                                            <button type="button" className="ns-lib-row text-ink-soft" onClick={() => setLocation({ view: 'shared', folderId: null })} title={`Everything ${group.by} shared with you`}>
                                                <span className="w-[18px] flex-none" aria-hidden="true" />
                                                <span className="truncate flex-1 min-w-0 text-xs">{group.workspaces.length - 3} more…</span>
                                            </button>
                                        </li>
                                    )}
                                </ul>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* At the foot: the cloud storage and the local folder, when there is one to speak of */}
            {(storage || localFolderName) && (
                <div className="ns-well mt-6 p-3 flex flex-col gap-2" data-sidebar-storage>
                    {storage && storage.limitBytes > 0 && (
                        <>
                            <div className="flex items-center justify-between gap-2 text-xs font-semibold">
                                <span>Cloud storage</span>
                                <span className="text-ink-soft tabular-nums">{formatBytes(storage.usedBytes)} of {formatBytes(storage.limitBytes)}</span>
                            </div>
                            <div className="ns-meter" data-tone={storage.usedBytes / storage.limitBytes > 0.9 ? 'danger' : storage.usedBytes / storage.limitBytes > 0.7 ? 'warn' : 'lavender'}>
                                <span style={{ width: `${Math.min(100, Math.round((storage.usedBytes / storage.limitBytes) * 100))}%` }} />
                            </div>
                        </>
                    )}
                    {localFolderName && (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-ok-deep min-w-0">
                            <HardDrive className="w-3.5 h-3.5 flex-none" aria-hidden="true" />
                            <span className="truncate" title={localFolderName}>Local folder connected · {localFolderName}</span>
                        </div>
                    )}
                </div>
            )}

            {/* The small print, at the foot of the sidebar: the page has no footer to scroll to */}
            <nav aria-label="Footer" className="mt-4 px-1 flex items-center gap-1 text-2xs text-ink-faint">
                <Link to="/about" className="px-1.5 py-1 rounded-md hover:text-ink hover:bg-cream-200 transition-colors">About</Link>
                <Link to="/terms" className="px-1.5 py-1 rounded-md hover:text-ink hover:bg-cream-200 transition-colors">Terms</Link>
                <Link to="/privacy" className="px-1.5 py-1 rounded-md hover:text-ink hover:bg-cream-200 transition-colors">Privacy</Link>
                <span className="ml-auto pr-1 whitespace-nowrap">© 2026</span>
            </nav>
        </nav>
    );

    const currentLabel = search ? 'Search' : location.view === 'library' ? (currentFolder ? currentFolder.name : 'All workspaces') : location.view === 'recent' ? 'Recent' : location.view === 'starred' ? 'Starred' : (sharedCurrent ? sharedCurrent.name : 'Shared with me');

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') { setSearch(''); e.currentTarget.blur(); }
        if (e.key === 'Enter') {
            const first = workspaces.find(w => matchesQuery(w.name, search));
            if (first && search.trim()) onOpenWorkspace(first.id, first.isLocal);
        }
    };

    const searchField = (inputRef: React.RefObject<HTMLInputElement> | undefined, className: string) => (
        <div className={`relative ${className}`}>
            <Search className="w-4 h-4 text-ink-soft absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
            <input
                ref={inputRef}
                type="search"
                role="searchbox"
                aria-label="Search workspaces and folders"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={onSearchKeyDown}
                className="ns-field pl-10 pr-9"
            />
            {search && (
                <span className="absolute right-1.5 inset-y-0 flex items-center">
                    <button type="button" className="ns-icon-btn ns-icon-btn-sm" aria-label="Clear search" onClick={() => { setSearch(''); inputRef?.current?.focus(); }}>
                        <X aria-hidden="true" />
                    </button>
                </span>
            )}
        </div>
    );

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <div className="h-screen bg-cream-100 text-ink flex flex-col relative overflow-hidden">
            {/* The window never scrolls: the workspaces scroll inside their own column, under a bar that stays */}
            <PageScrollLock />

            {/* The page bar of every page. Fixed, not sticky, here: this page clips what overflows sideways,
                which makes its root the box a sticky bar would stick to, and that box never scrolls. Edge to
                edge, with the canvas page's 16px gutter: the mark sits where the canvas's does, the account
                menu at the far right, and nothing is wasted at the sides of a wide screen. */}
            <header className="ns-pagebar fixed inset-x-0 px-4">
                <div className="w-full h-full flex items-center gap-3">
                    <button type="button" onClick={() => goToFolder(null)} className="flex items-center gap-2.5 rounded-full shrink-0" aria-label="Noodle Studio library">
                        <BrandMark size={32} />
                        <span className="text-[1.0625rem] font-bold tracking-[-0.02em] hidden md:inline">Noodle Studio</span>
                        <BetaTag className="hidden md:inline-flex" />
                    </button>
                    {searchField(searchRef, 'hidden sm:block flex-1 max-w-md ml-auto')}

                    <div className="flex items-stretch shrink-0 ml-auto sm:ml-0" role="group" aria-label="Create">
                        <button type="button" className="ns-btn ns-btn-primary ns-btn-sm ns-btn-flat rounded-r-none pr-3" onClick={() => openNewWorkspace(location.view === 'library' ? location.folderId : null)}>
                            <Plus className="w-4 h-4" aria-hidden="true" />
                            <span className="hidden sm:inline">New workspace</span>
                            <span className="sm:hidden">New</span>
                        </button>
                        <LibraryMenu label="More ways to create" items={newMenu} triggerClassName="ns-btn ns-btn-primary ns-btn-sm ns-btn-flat rounded-l-none px-2 border-l-white/20">
                            <ChevronDown className="w-4 h-4" aria-hidden="true" />
                        </LibraryMenu>
                    </div>

                    <div className="h-6 w-px bg-cream-300 hidden sm:block" aria-hidden="true" />
                    {bell}
                    <ProfileMenu user={user} isPro={isPro} onSignIn={onSignIn} onSubscription={onSubscription} onAnalytics={onAnalytics} onSettings={onSettings} onSignOut={onSignOut} />
                </div>
            </header>

            {/* The page fills the window: the sidebar on the left edge, as wide and as far in as the canvas page's
                library panel (16px in, 300px wide, the content from 328px), the workspaces across the rest */}
            <div className="w-full flex-1 min-h-0 pt-16 px-4 relative z-10">
                <div className="h-full lg:grid lg:grid-cols-[300px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:gap-3 py-4">
                    <aside className="hidden lg:block min-h-0">
                        {/* One glass panel as tall as the window, that scrolls on its own: fifty projects stay in reach */}
                        <div className="ns-glass ns-glass-strong ns-lib-side h-full flex flex-col"><div className="ns-lib-side-scroll">{sidebar}</div></div>
                    </aside>

                    {/* The workspaces scroll here, in their own column, never the page */}
                    {/* A wide gutter either side of the workspaces on a big screen; each view's head stays at the top while its lists scroll */}
                    <main id="main" ref={mainRef} className="min-w-0 h-full overflow-y-auto overflow-x-hidden overscroll-contain px-2 sm:px-6 lg:px-16 xl:px-[100px] pb-8">
                        {searchField(undefined, 'sm:hidden mb-3')}
                        {renderLoadError()}
                        <BetaNote className="mb-5" />
                        <div className="lg:hidden flex flex-wrap items-center gap-2 mb-5">
                            <button ref={drawerOpenerRef} type="button" className="ns-btn ns-btn-ghost ns-btn-sm flex-none" onClick={() => setDrawerOpen(true)} aria-haspopup="dialog" aria-expanded={drawerOpen}>
                                <FolderTreeIcon className="w-4 h-4" aria-hidden="true" />
                                <span className="truncate max-w-[160px]">{currentLabel}</span>
                            </button>
                            <span className="h-6 w-px bg-cream-300 flex-none" aria-hidden="true" />
                            {([
                                ['All', () => goToFolder(null), location.view === 'library' && !location.folderId],
                                ['Recent', () => setLocation({ view: 'recent', folderId: null }), location.view === 'recent'],
                                ['Starred', () => setLocation({ view: 'starred', folderId: null }), location.view === 'starred'],
                                ...(sharedCount + invitations.length > 0 ? [['Shared', () => setLocation({ view: 'shared', folderId: null }), location.view === 'shared'] as const] : []),
                            ] as ReadonlyArray<readonly [string, () => void, boolean]>).map(([label, go, active]) => (
                                <button key={label} type="button" className={`ns-chip ns-chip-btn flex-none ${active && !search ? 'ns-chip-lavender' : ''}`} aria-current={active && !search ? 'page' : undefined} onClick={go}>{label}</button>
                            ))}
                        </div>
                        {renderMain()}
                    </main>
                </div>
            </div>

            {/* (no footer: the small print is at the foot of the sidebar, and nothing is reached by scrolling the page) */}

            {drawerOpen && (
                <div className="lg:hidden">
                    {/* Above the page bar (30), under the menus of its rows (60) and under dialogs (100) */}
                    <div className="ns-modal-scrim z-40" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
                    <div ref={drawerRef} className="ns-lib-drawer z-50" role="dialog" aria-modal="true" aria-label="Browse library">
                        <div className="flex items-center justify-between mb-3">
                            <span className="ns-t-heading">Browse</span>
                            <button ref={drawerCloseRef} type="button" className="ns-icon-btn" aria-label="Close" onClick={() => setDrawerOpen(false)}>
                                <X aria-hidden="true" />
                            </button>
                        </div>
                        {sidebar}
                    </div>
                </div>
            )}

            {/* ── Dialogs ── */}
            <NameDialog
                open={dialog.kind === 'new-workspace'}
                title="New workspace"
                description="Give it a name. You can change it later."
                label="Name"
                placeholder="e.g. Sci-fi storyboard"
                submitLabel="Create"
                onClose={closeDialog}
                onSubmit={(name) => onCreateWorkspace(name, newLocation)}
                location={{ folders, counts, value: newLocation, onChange: setNewLocation }}
            />
            <NameDialog
                open={dialog.kind === 'new-folder'}
                title={dialog.kind === 'new-folder' && dialog.folderKind === 'project' ? 'New project' : 'New folder'}
                description={dialog.kind === 'new-folder' && dialog.parentId ? `Inside ${describeFolder(folders, dialog.parentId)}.` : 'Projects group workspaces and folders.'}
                label="Name"
                placeholder={dialog.kind === 'new-folder' && dialog.folderKind === 'project' ? 'e.g. Art direction' : 'e.g. Characters'}
                submitLabel="Create"
                onClose={closeDialog}
                onSubmit={submitNewFolder}
                validate={dialog.kind === 'new-folder' ? validateFolderName(dialog.parentId, dialog.folderKind) : undefined}
                color={dialog.kind === 'new-folder' && dialog.folderKind === 'project' ? { value: newColor, onChange: setNewColor } : undefined}
            />
            <NameDialog
                open={dialog.kind === 'rename-workspace'}
                title="Rename workspace"
                label="Name"
                initialValue={dialog.kind === 'rename-workspace' ? dialog.workspace.name : ''}
                submitLabel="Rename"
                onClose={closeDialog}
                onSubmit={async (name) => {
                    if (dialog.kind !== 'rename-workspace') return;
                    await onRenameWorkspace({ id: dialog.workspace.id, isLocal: dialog.workspace.isLocal }, name);
                }}
            />
            <NameDialog
                open={dialog.kind === 'rename-folder'}
                title={dialog.kind === 'rename-folder' && dialog.folder.kind === 'project' ? 'Rename project' : 'Rename folder'}
                label="Name"
                initialValue={dialog.kind === 'rename-folder' ? dialog.folder.name : ''}
                submitLabel="Rename"
                onClose={closeDialog}
                onSubmit={async (name) => { if (dialog.kind === 'rename-folder') await onRenameFolder(dialog.folder.id, name); }}
                validate={dialog.kind === 'rename-folder' ? validateFolderName(dialog.folder.parentId, dialog.folder.kind, dialog.folder.id) : undefined}
            />
            <ColorDialog
                open={dialog.kind === 'color-folder'}
                title="Project colour"
                value={dialog.kind === 'color-folder' ? (dialog.folder.color || 'lavender') : 'lavender'}
                onClose={closeDialog}
                onSubmit={async (c) => { if (dialog.kind === 'color-folder') await onRecolorFolder(dialog.folder.id, c); }}
            />
            <MoveDialog
                open={dialog.kind === 'move-workspace'}
                title={dialog.kind === 'move-workspace' ? `Move “${dialog.label}”` : 'Move'}
                description="Choose a project or folder."
                folders={folders}
                counts={counts}
                allowRoot
                rootCount={unfiled.length}
                disabledReason={(id) => (dialog.kind === 'move-workspace' && id === dialog.currentFolderId ? 'Already here' : undefined)}
                onClose={closeDialog}
                onMove={async (target) => {
                    if (dialog.kind !== 'move-workspace') return;
                    await onMoveWorkspaces(dialog.refs, target);
                    toast.success('Moved', `“${dialog.label}” is now in ${describeFolder(folders, target)}.`);
                }}
            />
            <MoveDialog
                open={dialog.kind === 'move-folder'}
                title={dialog.kind === 'move-folder' ? `Move “${dialog.folder.name}”` : 'Move'}
                description={`Folders can be nested ${MAX_FOLDER_DEPTH - 1} levels deep inside a project.`}
                folders={folders}
                counts={counts}
                allowRoot={false}
                disabledReason={(id) => (dialog.kind === 'move-folder' && id ? shortMoveReason(canMoveFolder(folders, dialog.folder.id, id).reason) : undefined)}
                onClose={closeDialog}
                onMove={async (target) => {
                    if (dialog.kind !== 'move-folder') return;
                    if (!target) throw new Error('Choose a project or folder to move it into.');
                    await moveFolderTo(dialog.folder, target);
                }}
            />
            <ConfirmDialog
                open={dialog.kind === 'delete-workspace'}
                title={dialog.kind === 'delete-workspace' ? `Delete “${dialog.workspace.name}”?` : 'Delete workspace?'}
                description="This removes the workspace and its canvas. It cannot be undone."
                confirmLabel="Delete workspace"
                danger
                onClose={closeDialog}
                onConfirm={async () => {
                    if (dialog.kind !== 'delete-workspace') return;
                    await onDeleteWorkspace({ id: dialog.workspace.id, isLocal: dialog.workspace.isLocal });
                    toast.success('Workspace deleted', `“${dialog.workspace.name}” was removed.`);
                }}
            />
            <ConfirmDialog
                open={dialog.kind === 'leave-shared'}
                title={dialog.kind === 'leave-shared' ? `Leave “${dialog.workspace.name}”?` : 'Leave workspace?'}
                description="You will lose access unless the owner shares it with you again."
                confirmLabel="Leave"
                danger
                onClose={closeDialog}
                onConfirm={async () => { if (dialog.kind === 'leave-shared') await onLeaveSharedWorkspace(dialog.workspace.id); }}
            />
            <ConfirmDialog
                open={dialog.kind === 'leave-project'}
                title={dialog.kind === 'leave-project' ? `Leave “${dialog.project.name}”?` : 'Leave project?'}
                description="You will no longer see this project or any of its workspaces, unless the owner shares it with you again."
                confirmLabel="Leave project"
                danger
                onClose={closeDialog}
                onConfirm={async () => {
                    if (dialog.kind !== 'leave-project' || !onLeaveSharedProject) return;
                    await onLeaveSharedProject(dialog.project.id);
                    setLocation({ view: 'shared', folderId: null });
                    toast.success('You left the project', `“${dialog.project.name}” is no longer in your library.`);
                }}
            />
            {sharingProject && (
                <ShareModal
                    isOpen
                    kind="project"
                    projectId={sharingProject.id}
                    projectName={sharingProject.name}
                    currentUserId={user.sub}
                    onClose={() => setSharingProject(null)}
                    onChanged={() => { void onRefresh(); }}
                />
            )}
            {onSendProjectCopy && onCancelProjectCopy && (
                <SendCopyDialog
                    open={!!copyingProject}
                    project={copyingProject}
                    folders={folders}
                    workspaces={workspaces}
                    isPro={isPro}
                    sent={copiesSent}
                    onSend={onSendProjectCopy}
                    onCancel={onCancelProjectCopy}
                    onClose={() => setCopyingProject(null)}
                    onSubscription={onSubscription}
                />
            )}
            <DeleteFolderDialog
                open={dialog.kind === 'delete-folder'}
                folder={dialog.kind === 'delete-folder' ? dialog.folder : null}
                folders={folders}
                descendantFolderCount={dialog.kind === 'delete-folder' ? getDescendantFolderIds(folders, dialog.folder.id).size : 0}
                workspaceCount={dialog.kind === 'delete-folder' ? counts.get(dialog.folder.id) || 0 : 0}
                onClose={closeDialog}
                onConfirm={confirmDeleteFolder}
            />
        </div>
    );
};
